import React from 'react';
import { HeaderSkeleton } from './HeaderSkeleton';

interface GradesSkeletonProps {
  /**
   * Number of rows to show in the skeleton table
   */
  rowCount?: number;

  /**
   * Additional class names
   */
  className?: string;
}

/**
 * GradesSkeleton component
 *
 * This component provides a loading skeleton specifically designed for the grades page.
 * It includes a header and a table with customizable number of rows.
 *
 * @example
 * <GradesSkeleton />
 */
export const GradesSkeleton: React.FC<GradesSkeletonProps> = ({
  rowCount = 5,
  className = '',
}) => {
  return (
    <div className={`min-h-screen bg-[#F5F8F9] ${className}`}>
      <div className="max-w-[1600px] mx-auto px-0 py-4">
        {/* Header Skeleton */}
        <div className="mb-6">
          <HeaderSkeleton buttonCount={1} />
        </div>

        {/* Table Skeleton */}
        <div className="bg-white rounded-xl overflow-hidden border border-[#E4E8EA] animate-pulse">
          {/* Table Header */}
          <div className="grid grid-cols-5 gap-4 p-4 bg-[#F5F8F9] border-b border-[#E4E8EA]">
            <div className="h-6 bg-gray-200 rounded-md"></div>
            <div className="h-6 bg-gray-200 rounded-md"></div>
            <div className="h-6 bg-gray-200 rounded-md"></div>
            <div className="h-6 bg-gray-200 rounded-md"></div>
            <div className="h-6 bg-gray-200 rounded-md"></div>
          </div>

          {/* Table Rows */}
          {[...Array(rowCount)].map((_, rowIndex) => (
            <div
              key={rowIndex}
              className="grid grid-cols-5 gap-4 p-4 border-b border-[#E4E8EA]"
              style={{ animationDelay: `${rowIndex * 0.05}s` }}
            >
              <div
                className="h-6 bg-gray-100 rounded-md"
                style={{ width: '80%' }}
              ></div>
              <div
                className="h-6 bg-gray-100 rounded-md"
                style={{ width: '60%' }}
              ></div>
              <div className="flex gap-1">
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className="h-6 w-8 bg-gray-100 rounded-md"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  ></div>
                ))}
              </div>
              <div
                className="h-6 bg-gray-100 rounded-md"
                style={{ width: '70%' }}
              ></div>
              <div
                className="h-6 bg-gray-100 rounded-md"
                style={{ width: '50%' }}
              ></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * GradesEmptySkeleton component
 *
 * This component provides a loading skeleton for when there are no grades to display.
 *
 * @example
 * <GradesEmptySkeleton />
 */
export const GradesEmptySkeleton: React.FC<{ className?: string }> = ({
  className = '',
}) => {
  return (
    <div className={`min-h-screen bg-[#F5F8F9] ${className}`}>
      <div className="max-w-[1600px] mx-auto px-6 py-4">
        {/* Header Skeleton */}
        <div className="mb-6">
          <HeaderSkeleton buttonCount={1} />
        </div>

        {/* Empty State Skeleton */}
        <div className="flex flex-col items-center justify-center h-[400px] bg-white rounded-xl p-8 border border-[#E4E8EA] animate-pulse">
          <div className="w-16 h-16 bg-gray-200 rounded-full mb-6"></div>
          <div className="h-7 w-48 bg-gray-200 rounded-md mb-3"></div>
          <div className="h-5 w-64 bg-gray-100 rounded-md mb-6"></div>
          <div className="h-10 w-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    </div>
  );
};
